import { useCallback, useEffect, useState } from "react";
import { App } from "./App";
import { api, HttpError, LEVEL_CAMERA_VIEWER, type MeResponse } from "./api/client";
import { LoginScreen, type SsoConfig } from "./screens/LoginScreen";

type State =
  | { kind: "checking" }
  | { kind: "anonymous"; sso: SsoConfig }
  | {
      kind: "authed";
      username: string;
      authEnabled: boolean;
      accessLevel: number;
    };

/**
 * Fall back to the lowest level when the server omits it. A missing level must
 * never read as "allow" — the API would refuse anyway, but the UI should not
 * offer controls it cannot honour.
 *
 * `me.admin` is ignored on purpose: it is `accessLevel >= 3` server-side, and
 * carrying the same fact down two channels invites a screen that gates on one
 * while another gates on the other.
 */
function authedFrom(me: MeResponse): Extract<State, { kind: "authed" }> {
  return {
    kind: "authed",
    username: me.username,
    authEnabled: me.authEnabled,
    accessLevel: typeof me.accessLevel === "number" ? me.accessLevel : LEVEL_CAMERA_VIEWER,
  };
}

/**
 * Probes /api/auth/me on mount; renders the LoginScreen if it returns 401,
 * the full App once authenticated. When the server reports authEnabled=false
 * (dev, tests) the check returns immediately and login is never shown.
 *
 * Logout falls back to anonymous and re-renders the login form. The 401 from
 * any subsequent request is what surfaces a stale session — see onUnauthorized.
 *
 * The 401 body carries googleEnabled/microsoftEnabled and their client ids so
 * the login screen can show the right SSO buttons without a second request.
 */
export function AuthGate() {
  const [state, setState] = useState<State>({ kind: "checking" });

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => {
        if (!cancelled) setState(authedFrom(me));
      })
      .catch((err) => {
        if (cancelled) return;
        const sso: SsoConfig = { google: null, microsoft: null };
        if (err instanceof HttpError && err.status === 401 && err.body) {
          try {
            const body = JSON.parse(err.body);
            if (body.googleEnabled && typeof body.googleClientId === "string") {
              sso.google = { clientId: body.googleClientId };
            }
            if (body.microsoftEnabled && typeof body.microsoftClientId === "string") {
              sso.microsoft = {
                clientId: body.microsoftClientId,
                tenant: typeof body.microsoftTenant === "string"
                  ? body.microsoftTenant
                  : "consumers",
              };
            }
          } catch {
            // body wasn't JSON — fine, just no SSO config
          }
        }
        setState({ kind: "anonymous", sso });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Land on the most restrictive view, then re-probe /me for the real level.
  // The login endpoints do report accessLevel now, but /me is the single place
  // that assembles the whole picture, and starting at camera-viewer means a
  // failed probe never over-grants.
  const onLoggedIn = useCallback((username: string) => {
    setState({
      kind: "authed",
      username,
      authEnabled: true,
      accessLevel: LEVEL_CAMERA_VIEWER,
    });
    api
      .me()
      .then((me) => setState(authedFrom(me)))
      .catch(() => {
        // Stay signed in at the lowest level; every action is gated
        // server-side regardless of what the UI offers.
      });
  }, []);

  const onLogout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // best-effort — cookie is HttpOnly so we can't clear it client-side
    }
    setState({ kind: "anonymous", sso: { google: null, microsoft: null } });
  }, []);

  if (state.kind === "checking") {
    return <div className="auth-checking">Loading…</div>;
  }
  if (state.kind === "anonymous") {
    return <LoginScreen onLoggedIn={onLoggedIn} sso={state.sso} />;
  }
  return (
    <App
      username={state.username}
      authEnabled={state.authEnabled}
      accessLevel={state.accessLevel}
      onLogout={onLogout}
    />
  );
}
